import { UseQueryStateResult } from '@reduxjs/toolkit/dist/query/react/buildHooks.d'
import { createApi } from '@reduxjs/toolkit/query/react'

import baseQueryInterceptor from '@api/base-query-interceptor'
import { ApiResponse } from '@api/types/api-response.type'

export type Coords3D = [number, number, number]

export enum ModelControlAction {
  ChangeScene = 'changeScene',
  RedirectBuilding = 'redirectBuilding',
}

export enum LoaderType {
  Bar = 'bar',
  Spinner = 'spinner',
}

export interface CameraControl {
  maxDistance?: number
  minDistance?: number
  minPolarAngle?: number
  maxPolarAngle?: number
  enablePan?: boolean
}

export enum ToneMapping {
  NoToneMapping = 'NoToneMapping',
  LinearToneMapping = 'LinearToneMapping',
  ReinhardToneMapping = 'ReinhardToneMapping',
  CineonToneMapping = 'CineonToneMapping',
  ACESFilmicToneMapping = 'ACESFilmicToneMapping',
  AgXToneMapping = 'AgXToneMapping',
  NeutralToneMapping = 'NeutralToneMapping',
  CustomToneMapping = 'CustomToneMapping',
}

export enum OutputColorSpace {
  NoColorSpace = 'NoColorSpace',
  SRGBColorSpace = 'SRGBColorSpace',
  LinearSRGBColorSpace = 'LinearSRGBColorSpace',
}

export enum PowerPreference {
  Default = 'default',
  HighPerformance = 'high-performance',
  LowPower = 'low-power',
}

export enum Precision {
  High = 'highp',
  Medium = 'mediump',
  Low = 'lowp',
}

export interface GlControl {
  toneMapping?: ToneMapping
  toneMappingExposure?: number
  outputColorSpace?: OutputColorSpace
  antialias?: boolean
  alpha?: boolean
  depth?: boolean
  powerPreference?: PowerPreference
  precision?: Precision | null
  premultipliedAlpha?: boolean
}

export interface EnvironmentControl {
  environmentIntensity?: number
  environmentRotation?: Coords3D | null
  backgroundOnly?: boolean
  disableBackground?: boolean
  backgroundIntensity?: number
  backgroundBlurriness?: number
  backgroundRotation?: Coords3D | null
  resolution?: number | null
}

export interface AmbientLightControl {
  active?: boolean
  intensity?: number
  color?: string
}

export interface CanvasControl {
  near?: number
  far?: number
}

export interface ModelControl {
  name: string
  label?: string
  actionTarget?: string
  action?: ModelControlAction
  hoverColor?: string
  hoverActive?: boolean
  position?: Coords3D
  rotation?: Coords3D
  scale?: number
}

export interface Loader {
  bg: string
  icon: string
  type: LoaderType
  color: string
}

export interface Model {
  id: string
  sceneId: string
  name: string
  autoRotate: boolean
  gltfSrc: string
  hasFiltering: boolean
  showLevelInfoOnHover: boolean
  hdr: string
  loader: Loader
  position: Coords3D
  prev?: string
  target: Coords3D
  visible: boolean
  cameraControl?: CameraControl
  glControl?: GlControl
  environmentControl?: EnvironmentControl
  ambientLightControl?: AmbientLightControl
  canvas?: CanvasControl
  modelControl: ModelControl[]
}

export interface Scene {
  id: string
  name: string
  projectId: string
  models: Model[]
}

export interface SceneList {
  current_page: number
  last_page: number
  per_page: number
  total: number
  next_page_url: string
  prev_page_url: string
  list: Scene[]
}

type SceneListParams = {
  projectIdOrName: string
  include?: string
  page?: number
  limit?: number
  filters?: {
    id?: string
    name?: string
  }
}

const API_VERSION = 'v1'

export const showcase3dApi = createApi({
  reducerPath: 'showcase3dApi',
  baseQuery: baseQueryInterceptor,
  endpoints: (builder) => ({
    getScenes: builder.query<ApiResponse<SceneList>, SceneListParams>({
      query: ({ projectIdOrName, ...params }: SceneListParams) => ({
        url: `/${API_VERSION}/3d/${projectIdOrName}/scenes`,
        method: 'get',
        params,
      }),
    }),
  }),
})

interface SceneListApiResult {
  scenes: SceneList
  isLoaded: boolean
  isError: boolean
  status: string
  errorStatus: number
  isFetching: boolean
}

export const selectFromSceneListResult = ({
  data,
  isLoading,
  isError,
  status,
  error,
  isFetching,
}: UseQueryStateResult<any, any>): SceneListApiResult => ({
  scenes: data?.data || [],
  isLoaded: !isLoading,
  isError,
  status,
  errorStatus: error?.status,
  isFetching,
})

export const { useGetScenesQuery } = showcase3dApi
